import { useEffect, useState } from "react";
import {
  Copy, KeyRound, Loader2, MailCheck, ShieldCheck, ShieldOff, LogIn, LogOut as LogOutIcon,
  Package, TicketPercent, FileDown, UserCheck, ReceiptText, ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import { api, adminCall, formatApiError } from "../../api";

const input =
  "w-full rounded-xl border border-sage/20 bg-white px-4 py-2.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-sage";

const EVENT_META = {
  sign_in: { icon: LogIn, label: "Signed in" },
  sign_in_failed: { icon: LogOutIcon, label: "Failed sign-in attempt" },
  product_created: { icon: Package, label: "Product created" },
  product_updated: { icon: Package, label: "Product updated" },
  product_deleted: { icon: Package, label: "Product deleted" },
  file_attached: { icon: FileDown, label: "Download file attached" },
  file_detached: { icon: FileDown, label: "Download file removed" },
  promo_created: { icon: TicketPercent, label: "Promo code created" },
  promo_updated: { icon: TicketPercent, label: "Promo code updated" },
  promo_deleted: { icon: TicketPercent, label: "Promo code deleted" },
  invite_created: { icon: MailCheck, label: "Owner invite created" },
  owner_registered: { icon: UserCheck, label: "Owner registered" },
  password_changed: { icon: KeyRound, label: "Password changed" },
  "2fa_enabled": { icon: ShieldCheck, label: "Two-factor enabled" },
  "2fa_disabled": { icon: ShieldOff, label: "Two-factor disabled" },
  "2fa_setup_started": { icon: ShieldCheck, label: "Two-factor setup started" },
  receipt_resent: { icon: ReceiptText, label: "Receipt re-sent" },
};

const ActivityLog = () => {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    adminCall(() => api.get("/admin/audit"))
      .then((r) => setEvents(r.data.events))
      .catch(() => setEvents([]));
  }, []);
  return (
    <div className="rounded-2xl border border-sage/12 bg-white/80 p-6" data-testid="activity-log">
      <h3 className="flex items-center gap-2 font-serif text-xl text-ink">
        <ScrollText size={18} className="text-sage" /> Activity log
      </h3>
      <p className="mt-1 text-xs text-ink/50">Latest 100 events — sign-ins, product changes, invites and security actions.</p>
      <div className="mt-4 max-h-96 space-y-1 overflow-y-auto pr-1">
        {events === null && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-sage" /></div>}
        {events?.length === 0 && <p className="py-6 text-center text-sm text-ink/45">Nothing recorded yet.</p>}
        {(events || []).map((e) => {
          const meta = EVENT_META[e.event] || { icon: ScrollText, label: e.event };
          return (
            <div key={e.id} className="flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-cream-dark/50" data-testid={`audit-${e.event}`}>
              <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${e.event === "sign_in_failed" ? "bg-red-50 text-red-500" : "bg-sage-tint text-sage"}`}>
                <meta.icon size={13} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink">
                  {meta.label}
                  {e.detail ? <span className="text-ink/55"> · {e.detail}</span> : null}
                </p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-ink/40">
                  {e.actor} · {new Date(e.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SecurityCard = ({ me, onChanged }) => {
  const [stage, setStage] = useState("idle"); // idle | confirm-enable | disable
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const enabled = !!me.twofa_enabled;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await adminCall(fn);
      toast.success(okMsg);
      setStage("idle");
      setCode("");
      setPassword("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-sage/12 bg-white/80 p-6" data-testid="security-card">
      <h3 className="flex items-center gap-2 font-serif text-xl text-ink">
        <ShieldCheck size={18} className="text-sage" /> Two-factor sign-in
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-ink/55">
        {enabled
          ? "On. Every sign-in needs your password plus a 6-digit code emailed to you."
          : "Off. Turn it on and every sign-in will also need a 6-digit code emailed to you."}
      </p>
      {stage === "idle" && (
        <div className="mt-4">
          {enabled ? (
            <button onClick={() => setStage("disable")} data-testid="twofa-disable-start"
              className="rounded-full border border-red-200 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-red-500 transition-colors hover:bg-red-500 hover:text-white">
              Turn off
            </button>
          ) : (
            <button
              onClick={() => run(() => api.post("/admin/2fa/start"), `Code emailed to ${me.email}`).then(() => setStage("confirm-enable"))}
              data-testid="twofa-enable-start"
              className="rounded-full bg-sage px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream transition-colors hover:bg-sage-deep"
            >
              Turn on — email me a code
            </button>
          )}
        </div>
      )}
      {stage === "confirm-enable" && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
            data-testid="twofa-enable-code" placeholder="6-digit code"
            className={`${input} font-mono tracking-[0.3em]`} />
          <button
            onClick={() => run(() => api.post("/admin/2fa/confirm", { code }), "Two-factor sign-in is on")}
            disabled={busy || code.length !== 6} data-testid="twofa-enable-confirm"
            className="rounded-full bg-sage px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream disabled:opacity-50">
            {busy ? "…" : "Confirm"}
          </button>
        </div>
      )}
      {stage === "disable" && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="twofa-disable-password" placeholder="Confirm with your password" className={input} />
          <button
            onClick={() => run(() => api.post("/admin/2fa/disable", { password }), "Two-factor sign-in is off")}
            disabled={busy || !password} data-testid="twofa-disable-confirm"
            className="rounded-full border border-red-300 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-red-500 disabled:opacity-50">
            {busy ? "…" : "Turn off"}
          </button>
        </div>
      )}
    </div>
  );
};

const PasswordCard = () => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await adminCall(() => api.post("/admin/change-password", { current_password: form.current, new_password: form.next }));
      toast.success("Password changed — use it next time you sign in");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-sage/12 bg-white/80 p-6" data-testid="password-card">
      <h3 className="flex items-center gap-2 font-serif text-xl text-ink">
        <KeyRound size={18} className="text-sage" /> Change password
      </h3>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <input type="password" required value={form.current} data-testid="pw-current"
          onChange={(e) => setForm({ ...form, current: e.target.value })} placeholder="Current password" className={input} />
        <input type="password" required minLength={10} value={form.next} data-testid="pw-new"
          onChange={(e) => setForm({ ...form, next: e.target.value })} placeholder="New password (10+ characters)" className={input} />
        <input type="password" required minLength={10} value={form.confirm} data-testid="pw-confirm"
          onChange={(e) => setForm({ ...form, confirm: e.target.value })} placeholder="Confirm new password" className={input} />
        <button disabled={busy} data-testid="pw-change-submit"
          className="flex items-center gap-2 rounded-full bg-sage px-6 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream transition-colors hover:bg-sage-deep disabled:opacity-60">
          {busy && <Loader2 size={13} className="animate-spin" />} Update password
        </button>
      </form>
    </div>
  );
};

export default function AccountPanel({ me, onMeChanged }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await adminCall(() =>
        api.post("/admin/invite", { email, origin_url: window.location.origin })
      );
      setInvite(data);
      toast.success(data.emailed ? "Invite created and emailed" : "Invite created — copy the link below");
    } catch (err) {
      toast.error(formatApiError(err, "Could not create the invite."));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(invite.invite_url);
    toast.success("Link copied");
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-account-panel">
      <div>
        <h2 className="font-serif text-2xl text-ink">Owner account</h2>
        <p className="mt-1 text-sm text-ink/50">
          Signed in as <span className="font-medium text-ink">{me.email}</span>. Only admin accounts
          can manage products, orders, files and promo codes — and only one admin can be active at a
          time.
        </p>
      </div>

      <SecurityCard me={me} onChanged={onMeChanged} />
      <PasswordCard />

      <div className="rounded-2xl border border-sage/12 bg-white/80 p-6">
        <h3 className="flex items-center gap-2 font-serif text-xl text-ink">
          <MailCheck size={18} className="text-sage" /> Invite a new owner
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-ink/55">
          Generate a one-time setup link bound to an email address. Whoever completes it becomes the
          owner — and every other account (including the one you're using now) is deactivated at
          that moment. Links expire after 24 hours.
        </p>
        <form onSubmit={generate} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="invite-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); setInvite(null); }}
            placeholder="Email to bind the invite to"
            className="flex-1 rounded-full border border-sage/20 bg-white px-5 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-sage"
          />
          <button
            data-testid="invite-generate-button"
            disabled={busy}
            className="flex items-center justify-center gap-2 rounded-full bg-sage px-6 py-3 text-xs font-medium uppercase tracking-[0.12em] text-cream transition-all hover:bg-sage-deep disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />}
            Generate invite
          </button>
        </form>

        {invite && (
          <div className="mt-5 rounded-xl border border-lavender/25 bg-lavender-tint/50 p-5" data-testid="invite-result">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lavender-deep">
              One-time setup link for {invite.email}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg bg-white/80 px-4 py-2.5 font-mono text-xs text-ink/70" data-testid="invite-link">
                {invite.invite_url}
              </code>
              <button onClick={copy} data-testid="invite-copy-button"
                className="rounded-full border border-lavender/40 p-2.5 text-lavender-deep transition-colors hover:bg-lavender hover:text-white" aria-label="Copy link">
                <Copy size={14} />
              </button>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-ink/50">
              {invite.emailed
                ? "We also emailed this link. It works once and expires in 24 hours — generating a new one cancels it."
                : "Email delivery failed, so copy and send this link yourself. It works once and expires in 24 hours."}
            </p>
          </div>
        )}
      </div>

      <ActivityLog />
    </div>
  );
}
